import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { IMAGES } from "../../data";
import { ArrowDownRight } from "lucide-react";

const LINES = ["Cars,", "Engineered", "Immaculate."];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 * i + 0.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ start }) => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const carY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const carScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const floatY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] w-full overflow-hidden spotlight"
    >
      {/* Parallax car background */}
      <motion.div
        style={{ y: carY, scale: carScale }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.heroCar}
          alt="Studio-lit performance car"
          className="h-full w-full object-cover object-center opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
      </motion.div>

      {/* Floating clipped frame */}
      <motion.div
        style={{ y: floatY }}
        initial={{ opacity: 0, x: 60 }}
        animate={start ? { opacity: 1, x: 0 } : {}}
        transition={{ delay: 1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-6 top-[22%] z-10 hidden w-[240px] lg:block xl:w-[300px]"
      >
        <div className="clip-tag overflow-hidden border border-white/10">
          <img
            src={IMAGES.detailing}
            alt="Detailing close up"
            className="h-[200px] w-full object-cover xl:h-[240px]"
          />
        </div>
        <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
          <span>/ ceramic finish</span>
          <span className="text-[var(--volt)]">9H</span>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-20 mx-auto flex min-h-[100svh] max-w-[1500px] flex-col justify-end px-6 pb-16 pt-32 md:px-12 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={start ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          className="mb-8 flex items-center gap-4 font-mono text-xs uppercase tracking-[0.3em] text-white/60"
        >
          <span className="h-px w-10 bg-[var(--volt)]" />
          German-Equipped Auto Studio · Est. Pan India
        </motion.div>

        <h1 className="font-display text-[15vw] font-black uppercase leading-[0.9] tracking-tighter text-white sm:text-[12vw] lg:text-[9vw]">
          {LINES.map((l, i) => (
            <span key={l} className="mask-line">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate={start ? "show" : "hidden"}
                className="inline-block"
              >
                {i === 2 ? (
                  <span className="text-stroke-volt">{l}</span>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-base leading-relaxed text-white/70 md:text-lg"
          >
            Service, repair and detailing on factory-calibrated German
            machinery — one obsessive standard, now live across Punjab and
            expanding pan India.
          </motion.p>

          <motion.a
            data-testid="hero-cta"
            href="#services"
            onClick={(e) => {
              e.preventDefault();
              if (lenis) lenis.scrollTo("#services");
              else document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="group flex items-center gap-4"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-300 group-hover:border-[var(--volt)] group-hover:bg-[var(--volt)] group-hover:text-black">
              <ArrowDownRight className="transition-transform duration-300 group-hover:rotate-45" />
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/70">
              Explore the work
            </span>
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
};
